/**
 * Which paths are public and which are the product.
 *
 * One list, two readers. `middleware.ts` uses it to decide who needs the
 * `lexai_token` cookie; `components/VioFab.tsx` uses it to decide where the
 * marketing mascot may appear. They have to agree: the mascot's rule is
 * "never inside the product shell", and "the product shell" is exactly the
 * set of paths the middleware protects. Until 2026-09-15 this lived inline in
 * `middleware.ts`, and the mascot would have needed a second hand-written
 * list of `(app)` prefixes — one that drifts the day someone adds a route to
 * one and not the other.
 *
 * The classification is fail-closed on purpose: a path that matches nothing
 * here is PRODUCT. A new page under `src/app/(app)/` is protected — and
 * mascot-free — without a change to this file, while a new top-level
 * MARKETING route has to be added below or it will demand sign-in, which is
 * noticed within the hour. `publicRoutes.test.ts` walks `src/app` and fails
 * a route the list classifies on the wrong side.
 *
 * This module has no imports. `middleware.ts` runs on the Edge runtime and
 * imports it by file path, so it must stay free of anything that pulls in
 * React, react-query or the browser — do not re-export it from `./index.ts`.
 */

export const AUTH_FORM_PATHS: ReadonlySet<string> = new Set([
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
]);

// Exact-path marketing routes ONLY — no sub-paths under these should be
// public. '/team' and '/integrations' are here (not in MARKETING_PREFIXES
// below) because the PRODUCT app also owns routes at those same top-level
// segments (/team/members, /integrations/webhooks, ...), which must stay
// behind the auth gate. The marketing pages at the bare paths have no
// sub-routes of their own, so an exact match is sufficient for them.
export const MARKETING_PATHS: ReadonlySet<string> = new Set([
  '/',
  '/scan', // free contract scanner tool
  '/team', // marketing About/Team page; product's team management moved to /team/overview to avoid this exact collision
  '/integrations', // marketing landing page; product's own integrations UI lives under /integrations/{webhooks,mobile,extension,overview}
]);

// Top-level marketing sections — every other g-lexvio route lives under one
// of these, with real sub-routes (e.g. /tools/gst-calculator,
// /features/[slug]) that also need to be public. Kept as a prefix list
// (not per-page) since new marketing pages are added far more often than
// new top-level sections. Do NOT add '/team' or '/integrations' here — see
// MARKETING_PATHS above.
export const MARKETING_PREFIXES: readonly string[] = [
  '/about',
  '/blog',
  '/book-demo',
  '/calendar',
  '/careers',
  '/changelog',
  '/contact',
  '/customers',
  '/faqs',
  '/features',
  '/glossary',
  '/investors',
  '/library',
  '/partners',
  '/practice-areas',
  '/press',
  '/pricing',
  '/product',
  '/roadmap',
  '/security',
  '/solutions',
  '/status',
  '/tools',
  '/trust',
  '/use-cases',
  '/why-lexvio',
];

const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  ...MARKETING_PATHS,
  ...AUTH_FORM_PATHS,
  '/auth/callback',
  '/accept-invite',
  '/wealthorbit-sso',
]);

const PUBLIC_PREFIXES: readonly string[] = [
  ...MARKETING_PREFIXES,
  '/scan/shared/',
  '/s/',
  '/portal/',
  // The anonymous privacy-rights portal: a person files a request or a
  // complaint with an organisation's privacy programme. The trailing slash
  // keeps the programme's own desk (`/privacy/*`) behind sign-in.
  '/privacy-portal/',
  '/legal/',
  '/api/',
];

// Product-app carve-outs inside a public marketing prefix. '/blog' is a
// MARKETING_PREFIX (the public blog index + /blog/[slug] posts), but the
// product's Blog v2 authoring surface lives at /blog/posts and /blog/profile
// (src/app/(app)/blog/**) and must stay behind the auth gate — the /team //
// /integrations collision inverted: marketing owns the prefix, the product
// owns two sub-paths. Segment-exact so a marketing post slug like
// 'posts-we-loved' is unaffected.
const PRODUCT_CARVEOUT_PREFIXES: readonly string[] = ['/blog/posts', '/blog/profile'];

/**
 * True when `pathname` needs no sign-in: the marketing site, the auth forms,
 * the token-addressed share pages, `/legal/*`, `/api/*`.
 *
 * Everything else is the product — including `/setup`, the post-auth gate,
 * and any path this file has never heard of.
 */
export function isPublicPath(pathname: string): boolean {
  if (
    PRODUCT_CARVEOUT_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  ) {
    return false;
  }
  if (PUBLIC_PATHS.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * The marketing mascot's question, stated in its own words: is this path
 * inside the authenticated product shell? The negation of `isPublicPath`,
 * not a second list — see the module comment for why there must not be one.
 *
 * A signed-in person browsing /pricing is NOT inside the product, so this
 * is keyed on the path and never on whether a session exists.
 */
export function isProductPath(pathname: string): boolean {
  return !isPublicPath(pathname);
}
